import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Button, Field } from '../ui.jsx';

const EMPTY = {
  targetId: '',
  platform: 'facebook',
  reporterName: '',
  reporterContact: '',
  message: '',
};

const REASONS = [
  { id: 'fake', label: 'حساب وهمي', text: 'هذا الحساب وهمي وينتحل هوية جهة/شخص آخر.' },
  { id: 'impersonation', label: 'انتحال شخصية', text: 'الصفحة/الحساب ينتحل شخصية رسمية أو شخصية معروفة.' },
  { id: 'scam', label: 'احتيال', text: 'المحتوى يحتوي على احتيال أو محاولة نصب على المستخدمين.' },
  { id: 'spam', label: 'سبام', text: 'الحساب ينشر محتوى مزعج وإعلانات غير مرغوبة بشكل متكرر.' },
  { id: 'hate', label: 'محتوى مسيء', text: 'المحتوى مسيء ويخالف سياسات المنصة.' },
  { id: 'other', label: 'سبب آخر', text: '' },
];

/** نموذج بلاغ داخلي — يُسجَّل في اللوحة فقط بدون إرسال لفيسبوك/قوقل */
export function ReportForm({ embedded = false, notify, prefillTargetId, onDone }) {
  const [settings, setSettings] = useState({ brand: '', publicNote: '' });
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [reasonId, setReasonId] = useState('fake');
  const [showExtra, setShowExtra] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const settingsUrl = embedded ? '/api/settings' : '/api/public/settings';
    api(settingsUrl)
      .then((d) => setSettings(d.settings))
      .catch(() => {});
    api('/api/public/targets').then((d) => {
      setTargets(d.targets);
      const preferred = prefillTargetId
        ? d.targets.find((t) => t.id === Number(prefillTargetId))
        : null;
      const auto = preferred || (d.targets.length === 1 ? d.targets[0] : null);
      if (auto) {
        setForm((f) => ({ ...f, targetId: String(auto.id), message: f.message || REASONS[0].text }));
      } else {
        setForm((f) => ({ ...f, message: f.message || REASONS[0].text }));
      }
    });
  }, [embedded, prefillTargetId]);

  useEffect(() => {
    if (!prefillTargetId) return;
    setForm((f) => ({ ...f, targetId: String(prefillTargetId) }));
    setDone(null);
  }, [prefillTargetId]);

  const pickReason = (r) => {
    setReasonId(r.id);
    setForm((f) => ({ ...f, message: r.text || '' }));
  };

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    if (!form.targetId) return setErr('اختر الرابط المستهدف أولاً.');
    const msg = form.message.trim() || REASONS.find((r) => r.id === reasonId)?.text || '';
    if (msg.trim().length < 5) return setErr('اختر سبب البلاغ أو اكتب ملاحظة قصيرة.');
    setBusy(true);
    setErr('');
    try {
      const d = await api('/api/public/reports', {
        method: 'POST',
        body: {
          ...form,
          message: msg,
          targetId: Number(form.targetId),
        },
      });
      setDone(d);
      setForm((f) => ({
        ...EMPTY,
        targetId: f.targetId,
        platform: f.platform,
        message: REASONS.find((r) => r.id === reasonId)?.text || REASONS[0].text,
      }));
      notify?.({ ok: true, msg: `تم تسجيل البلاغ #${d.report.id} داخل اللوحة` });
      onDone?.(d);
    } catch (e2) {
      setErr(e2.message || 'تعذر إرسال البلاغ.');
      notify?.({ ok: false, msg: e2.message });
    } finally {
      setBusy(false);
    }
  }

  const selected = targets.find((t) => String(t.id) === String(form.targetId));

  const body = (
    <form onSubmit={submit} className="quick-report">
      <div className="qr-step">
        <div className="qr-step-label">1. اختر الرابط المراد الإبلاغ عنه</div>
        {targets.length === 0 ? (
          <p className="error">
            لا توجد روابط مفعّلة — أضف رابطاً من تبويب «الروابط المستهدفة» أولاً.
          </p>
        ) : (
          <div className="target-picks">
            {targets.map((t) => (
              <button
                type="button"
                key={t.id}
                className={`target-pick${String(form.targetId) === String(t.id) ? ' on' : ''}`}
                onClick={() => setForm({ ...form, targetId: String(t.id) })}
              >
                <b>{t.title}</b>
                <span className="muted small linkish">{t.url}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="qr-step">
        <div className="qr-step-label">2. مصدر الرابط (للتصنيف فقط)</div>
        <div className="seg">
          {[
            ['facebook', 'فيسبوك', '📘'],
            ['google', 'قوقل', '🔍'],
          ].map(([v, l, i]) => (
            <button
              type="button"
              key={v}
              className={form.platform === v ? 'on' : ''}
              onClick={() => setForm({ ...form, platform: v })}
            >
              <span>{i}</span> {l}
            </button>
          ))}
        </div>
      </div>

      <div className="qr-step">
        <div className="qr-step-label">3. سبب البلاغ</div>
        <div className="reason-chips">
          {REASONS.map((r) => (
            <button
              type="button"
              key={r.id}
              className={`chip${reasonId === r.id ? ' on' : ''}`}
              onClick={() => pickReason(r)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Field label="نص سبب البلاغ" required hint="يمكنك اختيار سبب جاهز ثم تعديله أو كتابة نصك بالكامل">
          <textarea
            className="reason-box"
            rows={10}
            value={form.message}
            onChange={(e) => {
              setReasonId('other');
              setForm({ ...form, message: e.target.value });
            }}
            placeholder="اكتب سبب البلاغ بالتفصيل هنا..."
          />
        </Field>
      </div>

      <button type="button" className="extra-toggle" onClick={() => setShowExtra((v) => !v)}>
        {showExtra ? '▾ إخفاء التفاصيل الاختيارية' : '▸ تفاصيل اختيارية (اسم / تواصل)'}
      </button>

      {showExtra && (
        <div className="grid-2">
          <Field label="اسم المُبلِغ">
            <input
              value={form.reporterName}
              onChange={(e) => setForm({ ...form, reporterName: e.target.value })}
              placeholder="اختياري"
            />
          </Field>
          <Field label="وسيلة التواصل">
            <input
              value={form.reporterContact}
              onChange={(e) => setForm({ ...form, reporterContact: e.target.value })}
              placeholder="بريد أو رقم (اختياري)"
            />
          </Field>
        </div>
      )}

      {err && <p className="error">{err}</p>}

      {selected && (
        <p className="qr-summary muted small">
          سيُحفظ البلاغ على <b>{selected.title}</b> داخل اللوحة ضمن طلبات{' '}
          <b>{form.platform === 'google' ? 'قوقل' : 'فيسبوك'}</b>.
        </p>
      )}

      <div className="form-foot">
        <Button type="submit" variant="primary" disabled={busy || !targets.length}>
          {busy ? 'جارِ التسجيل...' : 'تسجيل البلاغ في اللوحة'}
        </Button>
      </div>
    </form>
  );

  if (embedded) {
    return (
      <>
        {done && (
          <div className="ok-note ok-note-actions">
            <div>
              ✅ تم تسجيل البلاغ <b>#{done.report.id}</b> داخل اللوحة — يظهر في تبويب «الطلبات».
            </div>
            <div className="actions">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setDone(null);
                  setErr('');
                  onDone?.(done, { goRequests: true });
                }}
              >
                عرض الطلبات
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setDone(null);
                  setErr('');
                }}
              >
                بلاغ آخر
              </Button>
            </div>
          </div>
        )}
        {body}
      </>
    );
  }

  if (done)
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">✅</div>
          <h1>تم تسجيل بلاغك</h1>
          <p className="muted">
            رقم الطلب <b>#{done.report.id}</b> — تم حفظه داخل النظام وسيراجعه فريق الإشراف.
          </p>
          <Button className="full" variant="primary" onClick={() => setDone(null)}>
            إرسال بلاغ آخر
          </Button>
        </div>
      </div>
    );

  return (
    <div className="auth-page">
      <div className="auth-card wide">
        <div className="auth-logo">📣</div>
        <h1>{settings.brand || 'نظام البلاغات'}</h1>
        <p className="muted">
          {settings.publicNote || 'أرسل بلاغك ليُسجَّل داخل اللوحة ويراجعه فريق الإشراف.'}
        </p>
        {body}
      </div>
    </div>
  );
}

export default function Report() {
  return <ReportForm />;
}
